import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ChatCard, ChatCardProps } from '@/components/shared/ChatCard';
import {
	ASAPH_BOT_DISPLAY_NAME,
	ASAPH_BOT_USER_ID,
	LinkFrom,
	unknownDisplayName,
} from '@/config/constants';
import { directMessageUrl } from '@/config/urls';
import { Chat, ChatType, ListMyChatsQuery, User } from '@/graphql';
import { getChatTitleMembersRecipient } from '@/helpers/getChatChannelInfo';
import { getTimePassedAfterTimestamp } from '@/helpers/timeFormatter';
import { useAuth } from '@/providers/AuthProvider';
import { useIsMounted } from 'hooks/useIsMounted';
import { isEmpty, isNil, last } from 'ramda';

import styles from './DirectMessagesCard.module.scss';

type DirectMessagesCardProps = {
	chat: ListMyChatsQuery['listMyChats'][number];
	avatarOnly?: boolean;
	isUnread?: boolean;
	noLink?: boolean;
	chatCardProps?: Pick<ChatCardProps, 'selectable' | 'checked' | 'onClick'>;
};

export const DirectMessagesCard: React.FC<DirectMessagesCardProps> = ({
	chat,
	avatarOnly,
	isUnread,
	noLink,
	chatCardProps,
}) => {
	const { user } = useAuth();

	const type = useMemo(
		() => (chat.type === ChatType.Group ? 'group' : 'private'),
		[chat],
	);

	const { title, membersNames, recipientIds, userIds } =
		getChatTitleMembersRecipient(chat, user.id, user.displayName);
	const lastMessage = last(chat?.messages?.items || []);
	const isLastMessageByCurrentUser = lastMessage?.user?.id === user.id;
	const isLastMessageByBot = lastMessage?.userId === ASAPH_BOT_USER_ID;
	const lastMessageAuthor = isLastMessageByCurrentUser
		? 'You'
		: isLastMessageByBot
		? ASAPH_BOT_DISPLAY_NAME
		: (lastMessage?.user?.displayName || unknownDisplayName).split(' ')[0];

	return (
		<div className={styles['root']}>
			<ChatCard
				isOnline={false}
				{...{
					link: noLink
						? undefined
						: `${directMessageUrl.replace(':token', chat.id)}`,
					linkFrom: LinkFrom.messages,
					type,
					membersNames,
					chatTitle: title,
					lastMessage: lastMessage?.content || 'No messages yet',
					lastMessageAuthor,
					lastMessageTimestamp: getTimePassedAfterTimestamp(
						lastMessage?.createdAt || chat.createdAt,
					),
					isAuthorHidden:
						!isLastMessageByCurrentUser && type === 'private',
					avatarOnly,
					hasNotification: isUnread,
					recipientIds,
					userIds,
				}}
				{...chatCardProps}
			/>
		</div>
	);
};
